import { useCallback, useEffect, useMemo, useRef, useState} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col } from "reactstrap";

import {
  faChalkboardUser,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import swal from "sweetalert";
import { APIMS } from "../../../../../config/apims";
import { APISM } from "../../../../../config/apism";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import ToastError from "../../../../NotificationToast/ToastError";
import Pagination from "../../../../components/pagination_new";


export default function Guru() {
  document.title = "Guru";

  const [getData, setGetData] = useState([]);

  const token = localStorage.getItem("token");
  const storageBranch = localStorage.getItem("ysb_branch_id");
  const storageLevel = localStorage.getItem("level");

  const [page] = useState(1);
  const [limit] = useState(1000);
  const [ascending] = useState(1);
  const [descending] = useState(0);
  const [keyword] = useState("");

  const [searchKeyword, setSearchKeyword] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFetching, setIsFetching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFilterDirty, setIsFilterDirty] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const [loading, setLoading] = useState(false);
  const [loadingSchool, setLoadingSchool] = useState(false);

  const firstLoadDone = useRef(false);
  const initSchoolDone = useRef(false);

  const [getDataSemesterMap, setGetDataSemesterMap] = useState([]);
  const [getDataBranch, setGetDataBranch] = useState([]);
  const [getDataSchool, setGetDataSchool] = useState([]);

  const [namaTA, setNamaTA] = useState("");
  const [namaSmstr, setNamaSmstr] = useState("");

  const [formFilter, setFormFilter] = useState({
    ysb_semester_id: "",
    ysb_branch_id: "",
    ysb_school_id: "",
  });

  const location = useLocation();
  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const storageLevel = localStorage.getItem("level");
    const storageItems = JSON.parse(localStorage.getItem("role_permission")) || [];

    if (storageLevel === "developer" || storageLevel === "superadmin") {
      setPermission({
        create: 1,
        read: 1,
        update: 1,
        delete: 1,
      });
    } else {
      const foundPermission = storageItems.find(
        (item) => item.menu?.url === location.pathname
      );

      if (!foundPermission) {
        navigate("/dashboard");
      } else {
        setPermission({
          create: foundPermission.create,
          read: foundPermission.read,
          update: foundPermission.update,
          delete: foundPermission.delete,
        });
      }
    }
  }, [location.pathname, navigate]);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );


  const isMounted = useRef(true);
  const abortControllers = useRef([]);

  useEffect(() => {
     isMounted.current = true;
     return () => {
       isMounted.current = false;
       abortControllers.current.forEach((c) => c.abort());
     };
   }, []);
 
  const schoolRequestRef = useRef(0);

  // SORT sekolah: SD → SMP → SMA
  const sortSchools = (schools) => {
    const order = ["SD", "SMP", "SMA"];
    return [...schools].sort((a, b) => {
      const jenjangA = order.findIndex((j) => (a.school_name || "").toUpperCase().includes(j));
      const jenjangB = order.findIndex((j) => (b.school_name || "").toUpperCase().includes(j));
      return jenjangA - jenjangB;
    });
  };

  const removeBasementBekasi = (schools, branchId) => {
    if (branchId === "BKS") return schools.filter((s) => s.school_code !== "BASEMENT_BEKASI");
    return schools;
  };

  // =========================
  // INIT: Branch + Semester 
  // =========================
  const fetchInit = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();
    abortControllers.current.push(controller);

    try {
      const [branchRes, semesterRes] = await Promise.all([
        APIMS.get(
          `/api/access/branches?page=${page}&limit=${limit}&ascending=${ascending}&search=${keyword}&branch=${storageBranch}&level=${storageLevel}`,
          { ...fetchParams, signal: controller.signal }
        ),
        APIMS.get(
          `/api/access/semesters?page=${page}&limit=${limit}&ascending=${descending}&search=${keyword}`,
          { ...fetchParams, signal: controller.signal }
        ),
      ]);

      if (!isMounted.current) return;

      // Branch default pertama
      if (branchRes?.status === 200) {
        const branches = branchRes.data.data || [];
        setGetDataBranch(branches);
        if (branches.length > 0) {
          const firstBranch = branches[0].branch_code;
          setFormFilter((prev) => ({ ...prev, ysb_branch_id: firstBranch }));
        }
      }

      // Semester aktif
      if (semesterRes?.status === 200) {
        const semesters = semesterRes.data.data || [];
        const active = semesters.find((s) => Number(s.status) === 1) || semesters[0];
        setGetDataSemesterMap(semesters);
        if (active) {
          setNamaTA(active.name_year || "");
          setNamaSmstr(active.semester || "");
          setFormFilter((prev) => ({ ...prev, ysb_semester_id: active.id }));
        }
      }
    } catch (err) {
      if (controller.signal.aborted || !isMounted.current) return;
      setLoading(false);
      swal({
        title: "Failed",
        text: err?.response?.data?.message || err.message || "Error",
        icon: "error",
      });
    } 

  }, [ascending, descending, fetchParams, keyword, limit, page, storageBranch, storageLevel]);

  useEffect(() => {
    fetchInit();
  }, [fetchInit]);

  // =========================
  // Fetch sekolah saat branch berubah
  // =========================
  const fetchSchoolsByBranch = useCallback(
    async (branchId) => {
      const requestId = ++schoolRequestRef.current;
      initSchoolDone.current = false;
      setLoadingSchool(true); // ⬅️ school mulai load
      try {
        const res = await APIMS.get(
          `/api/access/schools?page=${page}&limit=${limit}&ascending=${ascending}&branch=${branchId}&level=${storageLevel}`,
          fetchParams
        );
        if (!isMounted.current || requestId !== schoolRequestRef.current) return;
        if (res?.status === 200) {
          let schools = res.data.data || [];

          // ✅ FILTER ULANG UNTUK ROLE DEVELOPER
          if (storageLevel === "developer" || storageLevel === "superadmin") {
            schools = schools.filter(
              (s) => s.ysb_branch_id === branchId
            );
          }

          schools = removeBasementBekasi(schools, branchId);
          schools = sortSchools(schools);

          setGetDataSchool(schools);

          // default sekolah pertama
          if (schools.length > 0) {
            setFormFilter((prev) => ({
              ...prev,
              ysb_school_id: schools[0].school_code,
            }));
          } else {
            setFormFilter((prev) => ({ ...prev, ysb_school_id: ""}));
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
          if (isMounted.current && requestId === schoolRequestRef.current) {
          initSchoolDone.current = true;
          setLoadingSchool(false); // ⬅️ school SELESAI
          setLoading(false);       // ⬅️ shimmer BARU MATI DI SINI
          }
      }
    },[ascending, fetchParams, limit, page, storageLevel]
  );

  useEffect(() => {
    if (formFilter.ysb_branch_id) fetchSchoolsByBranch(formFilter.ysb_branch_id);
  }, [formFilter.ysb_branch_id, fetchSchoolsByBranch]);

  // =========================
  // Filter complete
  // =========================
  const isFilterComplete =
    !loadingSchool &&
    !!formFilter.ysb_semester_id &&
    !!formFilter.ysb_branch_id &&
    !!formFilter.ysb_school_id;

  // =========================
  // Fetch table data
  // =========================
  const GetResponseData = useCallback(async () => {
    setSearchKeyword("");
    setTablePage(1);
    setIsFetching(true);

    if (
      !formFilter.ysb_semester_id ||
      !formFilter.ysb_branch_id ||
      !formFilter.ysb_school_id 
    ) return;

    const params = {
      page: 1,
      limit: 1000,
      ascending: 1,
      search: "",
      ysb_semester_id: formFilter.ysb_semester_id,
      level: storageLevel,
      branch: formFilter.ysb_branch_id,
      ysb_school_id: formFilter.ysb_school_id
    };

    try {
      const response = await APISM.get(
        "/api/access/teachers",
        {
        params,
        ...fetchParams,
        }
      );

      if (response?.status === 200) {
        setGetData(response.data.data || []);
      }
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title:
          error.response?.data?.message ||
          "Gagal memuat data",
      });
    } finally {
      setIsFetching(false);
    }
  }, [formFilter, fetchParams, storageLevel]);


  // AUTO LOAD
  useEffect(() => {
    const schoolReady =
      initSchoolDone.current &&
      !loadingSchool &&
      !!formFilter.ysb_school_id;

    if (
      firstLoadDone.current ||
      !formFilter.ysb_semester_id ||
      !formFilter.ysb_branch_id ||
      !schoolReady 
    ) return;

    firstLoadDone.current = true;

    (async () => {
      setIsFetching(true);
      await GetResponseData();
      setHasSearched(true);
      setIsFirstLoad(false);
      setIsFilterDirty(false);
      setIsFetching(false);
    })();
  }, [
    formFilter.ysb_semester_id,
    formFilter.ysb_branch_id,
    formFilter.ysb_school_id,
    loadingSchool
  ]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormFilter((prev) => ({ ...prev, [name]: value }));

    if (!isFirstLoad) {
      setIsFilterDirty(true);
      setHasSearched(false);
    }
  };

  // =========================
  // BUTTON TAMPILKAN DATA
  // =========================
  const handleCari = async () => {
    // validasi (biar user jelas)
    if (loadingSchool ) {
      swal({
        title: "Mohon tunggu",
        text: "Data sekolah sedang dimuat. Coba lagi sebentar.",
        icon: "info",
        timer: 2200,
        buttons: false,
      });
      return;
    }

    if (!formFilter.ysb_semester_id || !formFilter.ysb_branch_id || !formFilter.ysb_school_id) {
      swal({
        title: "Filter belum lengkap",
        text: "Pastikan Semester, Cabang, dan sekolah sudah dipilih.",
        icon: "warning",
      });
      return;
    }

    setGetData([]);
    setHasSearched(false);
    setIsFetching(true);
    setSearchKeyword("");

    try {
      await GetResponseData();
      setHasSearched(true);
      setIsFilterDirty(false);
    } catch (err) {
      swal({
        title: "Failed",
        text: err?.response?.data?.message || "Terjadi kesalahan",
        icon: "error",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const nameCollator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    []
  );
   
  const filteredData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();

    const result = !keyword
      ? getData
      : getData.filter((item) => {
          const name = String(
            item?.full_name || ""
          ).toLowerCase();

          const nipYpi = String(
            item?.nip_ypi || ""
          ).toLowerCase();

          const email = String(
            item?.email || ""
          ).toLowerCase();

          return (
            name.includes(keyword) ||
            nipYpi.includes(keyword) ||
            email.includes(keyword)
          );
        });

    return [...result].sort((a, b) =>
      nameCollator.compare(
        String(a?.full_name || "").trim(),
        String(b?.full_name || "").trim()
      )
    );
  }, [getData, searchKeyword, nameCollator]);
 

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  useEffect(() => {
    setTablePage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const paginatedData = useMemo(() => {
    const safeRows = Math.max(1, rowsPerPage);
    const start = (tablePage - 1) * safeRows;
    return filteredData.slice(start, start + safeRows);
  }, [filteredData, tablePage, rowsPerPage]);

  const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

  const toTitleCaseGelar = (value) => {
    const s = safeStr(value).toLowerCase().trim();
    if (!s) return "";

    return s
      .split(" ")
      .map((word) => {
        // khusus gelar yang ada titik, contoh: s.si. -> S.Si.
        if (word.includes(".")) {
          return word
            .split(".")
            .map((part) => {
              if (!part) return "";
              return part.charAt(0).toUpperCase() + part.slice(1);
            })
            .join(".");
        }

        // normal title case, contoh: helmy -> Helmy
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  return (
    <>

      <div className="body sims-page">

        <div className="page-header-card">
          <div className="page-header-top d-flex">
            <div className="page-title-wrap">
              <span className="page-title-icon">
                <FontAwesomeIcon icon={faChalkboardUser}/>
              </span>
              <h4 className="page-title-text"> Data Guru </h4>
            </div>
          </div>
        </div>

        <div className="body-content sims-content">
          
          {/* {loading ? (
              <div className="teacher-skeleton-grid">
                <div className="teacher-shimmer teacher-skeleton-field" />

                <div className="teacher-shimmer teacher-skeleton-field" />

                <div className="teacher-shimmer teacher-skeleton-field" />

                <div className="teacher-shimmer teacher-skeleton-button" />
              </div>
            ) : ( */}
          {loading ? (
            <div className="sims-filter-card sims-filter-card-two-shimmer">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="shimmer sims-filter-skeleton" />
              ))}
            </div>
          ) : (
            <div className="sims-filter-card sims-filter-card-two">
                <div className="sims-filter-field">
                  <label
                    className="sims-filter-label"
                  >
                    Semester
                  </label>

                  <select
                    className="sims-filter-select"
                    name="ysb_semester_id"
                    value={formFilter.ysb_semester_id}
                    onChange={handleChange}
                  >
                    <option value="" hidden>
                      {namaTA} (Semester {namaSmstr})
                    </option>
                    {/* {getDataSemesterMap.map((user, index) => (
                      <option key={index} value={user?.id}>
                        {user?.name_year} (Semester {user?.semester})
                      </option>
                    ))} */}

                    {getDataSemesterMap.map((user) => (
                      <option key={user?.id} value={user?.id}>
                        {user?.name_year} (Semester {user?.semester})
                        {Number(user?.status) === 1 ? " • Aktif" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sims-filter-field">
                  <label
                    className="sims-filter-label"
                  >
                    Cabang
                  </label>

                  <select
                    className="sims-filter-select"
                    name="ysb_branch_id"
                    value={formFilter.ysb_branch_id}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      hidden
                    >
                      Pilih Cabang
                    </option>

                    <option value="" hidden>
                      Pilih Cabang
                    </option>
                    {getDataBranch.map((user, index) => (
                      <option key={index} value={user?.branch_code}>
                        {user?.branch_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sims-filter-field">
                  <label
                    className="sims-filter-label"
                  >
                    Sekolah
                  </label>

                  <select
                    className="sims-filter-select"
                    name="ysb_school_id"
                    value={formFilter.ysb_school_id}
                    onChange={handleChange}
                    disabled={loadingSchool}
                  >
                      <option value="" hidden>
                        {loadingSchool ? "Memuat data sekolah..." : "Pilih Sekolah"}
                      </option>

                      {!loadingSchool &&
                        getDataSchool.map((user, index) => (
                          <option key={index} value={user?.school_code}>
                            {user?.school_name}
                          </option>
                      ))}
                  </select>
                </div>

                <div className="sims-filter-action">
                  <button
                    type="button"
                    className="sims-filter-button"
                    onClick={handleCari}
                    disabled={
                      !isFilterComplete ||
                      isFetching
                    }
                  >
                    Tampilkan Data
                  </button>
                </div>
              </div>
          )}
          

          {isFilterDirty && !hasSearched && !isFetching && (
            <div
              role="status"
              style={{
                padding: "20px 16px",
                textAlign: "center",
                color: "#93969a",
                background: "#f8faff",
                border: "1px solid #e5eaf4",
                borderRadius: "12px",
                marginBottom: "16px",
                fontSize: "13px",
              }}
            >
              Silakan klik <strong>Tampilkan Data</strong> untuk menampilkan data sesuai filter.
            </div>
          )}

          {isFetching && (
            <div className="sims-loading">
              <PulseLoader color="#3f5bea" size={12} />
            </div>
          )}

          {hasSearched &&
            !isFetching && (
              <>
                <div className="sims-search-wrap mb-3">
                   <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="sims-search-icon"
                  />

                  <input
                    type="text"
                    className="sims-search-input"
                    placeholder="Cari nama guru, NIP YPI atau email..."
                    value={searchKeyword}
                    onChange={(event) => {
                      setSearchKeyword(event.target.value);
                      setTablePage(1);
                    }}
                  />
                </div>

                <div className="sims-table-card">
                  <div className="sims-table-scroll">
                    <table className="sims-table">
                      <thead>
                        <tr>
                          <th className="sims-col-number">No</th>
                          <th style={{width: "200px"}}>NIP YPI</th>
                          <th>Nama Guru</th>
                          <th>Email</th>
                        </tr>
                      </thead>

                      <tbody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((row, index) => (
                            <tr key={row?.id || index}>
                              <td className="sims-col-number">
                                <span className="sims-number-badge">
                                  {(tablePage - 1) * rowsPerPage + index + 1}
                                </span>
                              </td>

                              <td>
                                <span className="sims-text-bold">
                                  {row.nip_ypi || "-"}
                                </span>
                              </td>

                              <td>
                                <span className="sims-text-bold">
                                  {toTitleCaseGelar(row.full_name || "-")}
                                </span>
                              </td>

                              <td>
                                <span >
                                  {row.email || "-"}
                                </span>
                              </td>

                             
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="sims-empty-state">
                              <div className="sims-empty-state-content">
                                Data guru tidak ditemukan
                              </div>
                            
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                <Pagination
                  totalRows={filteredData.length}
                  tablePage={tablePage}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setTablePage}
                  onRowsPerPageChange={(value) => {
                    setRowsPerPage(value);
                    setTablePage(1);
                  }}
                />
                </div>
              </>
            )}
        </div>
      </div>
    </>
  );
}